import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { SITE } from "../data/site";
import { Mail, Phone, Linkedin, ArrowUpRight } from "lucide-react";

function RevealLine({ children, delay = 0 }) {
  return (
    <span
      className="block overflow-hidden"
      style={{ lineHeight: 1.15, paddingTop: "0.05em", paddingBottom: "0.05em" }}
    >
      <motion.span
        initial={{ y: "115%", opacity: 0 }}
        animate={{ y: "0%", opacity: 1 }}
        transition={{ delay: delay + 0.25, duration: 1.0, ease: [0.65, 0, 0.35, 1] }}
        className="block will-change-transform"
        style={{ lineHeight: 1 }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Contact() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const glowY = useTransform(scrollYProgress, [0, 1], ["-20%", "20%"]);

  return (
    <section
      id="contact"
      ref={ref}
      className="relative py-24 md:py-40 px-6 md:px-10 overflow-hidden bg-[var(--bg)]"
      data-testid="contact-section"
    >
      {/* Soft gold aura */}
      <motion.div
        aria-hidden
        style={{ y: glowY }}
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[80vw] h-[80vw] max-w-[900px] max-h-[900px] rounded-full opacity-[0.09]"
      >
        <div className="w-full h-full rounded-full bg-[radial-gradient(circle_at_center,#c9a24b_0%,transparent_60%)]" />
      </motion.div>

      <div className="relative mx-auto max-w-[1600px]">
        <div className="flex items-center gap-4 text-[11px] tracking-[0.32em] uppercase text-[var(--ink-dim)] mb-8">
          <span className="w-10 h-px bg-[var(--gold)]" />
          <span className="text-[var(--gold)] font-mono">05</span>
          <span>Let’s Begin</span>
        </div>

        <h2
          className="font-display text-[13vw] sm:text-[11vw] md:text-[10vw] tracking-[-0.02em]"
          data-testid="contact-headline"
          style={{ lineHeight: 1 }}
        >
          <RevealLine>Let’s make</RevealLine>
          <RevealLine delay={0.1}>something</RevealLine>
          <RevealLine delay={0.2}>
            <span className="italic text-[var(--gold)] font-light">
              extraordinary.
            </span>
          </RevealLine>
        </h2>

        <div className="mt-16 md:mt-24 grid grid-cols-12 gap-8 md:gap-10">
          <div className="col-span-12 md:col-span-5">
            <p className="text-[16px] md:text-[18px] leading-[1.6] text-[var(--ink-dim)] max-w-[42ch]">
              Bring us your ambition. A repositioning, a new category, an
              inflection you can feel but can’t yet name. We’ll respond within
              one working day.
            </p>
            <div className="mt-10 font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--ink-mute)]">
              Office of the Founding Partner
              <br />
              Dinkar Chopra
            </div>
          </div>

          <div className="col-span-12 md:col-span-6 md:col-start-7 flex flex-col divide-y divide-white/10 border-y border-white/10">
            <a
              href={`mailto:${SITE.contact.email}`}
              className="group flex flex-col md:grid md:grid-cols-12 md:items-center gap-2 md:gap-4 py-5 md:py-6 hover:bg-white/[0.02] transition-colors duration-500"
              data-testid="contact-email-link"
            >
              <div className="flex items-center gap-4 md:contents">
                <span className="md:col-span-1 font-mono text-[10px] tracking-[0.3em] text-[var(--gold)]">
                  01
                </span>
                <span className="md:col-span-2 flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-[var(--ink-dim)]">
                  <Mail size={14} /> Email
                </span>
              </div>
              <span className="md:col-span-8 font-display text-xl md:text-3xl transition-transform duration-500 md:group-hover:translate-x-2 break-all md:truncate">
                {SITE.contact.email}
              </span>
              <span className="hidden md:flex md:col-span-1 justify-end">
                <ArrowUpRight
                  size={18}
                  className="text-[var(--ink-mute)] group-hover:text-[var(--gold)] transition-colors"
                />
              </span>
            </a>

            <a
              href={`tel:${SITE.contact.phoneRaw}`}
              className="group flex flex-col md:grid md:grid-cols-12 md:items-center gap-2 md:gap-4 py-5 md:py-6 hover:bg-white/[0.02] transition-colors duration-500"
              data-testid="contact-phone-link"
            >
              <div className="flex items-center gap-4 md:contents">
                <span className="md:col-span-1 font-mono text-[10px] tracking-[0.3em] text-[var(--gold)]">
                  02
                </span>
                <span className="md:col-span-2 flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-[var(--ink-dim)]">
                  <Phone size={14} /> Phone
                </span>
              </div>
              <span className="md:col-span-8 font-display text-xl md:text-3xl transition-transform duration-500 md:group-hover:translate-x-2">
                {SITE.contact.phone}
              </span>
              <span className="hidden md:flex md:col-span-1 justify-end">
                <ArrowUpRight
                  size={18}
                  className="text-[var(--ink-mute)] group-hover:text-[var(--gold)] transition-colors"
                />
              </span>
            </a>

            <a
              href={SITE.contact.linkedin}
              target="_blank"
              rel="noreferrer"
              className="group flex flex-col md:grid md:grid-cols-12 md:items-center gap-2 md:gap-4 py-5 md:py-6 hover:bg-white/[0.02] transition-colors duration-500"
              data-testid="contact-linkedin-link"
            >
              <div className="flex items-center gap-4 md:contents">
                <span className="md:col-span-1 font-mono text-[10px] tracking-[0.3em] text-[var(--gold)]">
                  03
                </span>
                <span className="md:col-span-2 flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-[var(--ink-dim)]">
                  <Linkedin size={14} /> LinkedIn
                </span>
              </div>
              <span className="md:col-span-8 font-display text-xl md:text-3xl transition-transform duration-500 md:group-hover:translate-x-2 break-all md:truncate">
                {SITE.contact.linkedinLabel}
              </span>
              <span className="hidden md:flex md:col-span-1 justify-end">
                <ArrowUpRight
                  size={18}
                  className="text-[var(--ink-mute)] group-hover:text-[var(--gold)] transition-colors"
                />
              </span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
